import React from 'react'
import { Avatar, Button, MessageEditor, RightDrawer } from '@/shared/ui'
import { BottomDrawer } from '@/shared/ui/BottomDrawer'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { useUserDateFormatter } from '@/modules/auth/hooks/useUserTimezone'
import { useCommunityThread } from '../hooks/useCommunityThread'
import { useCommunityCardReactions } from '../hooks/useCommunityCardReactions'
import { useCommunityDraftAttachments } from '../hooks/useCommunityDraftAttachments'
import { useCommunityModeratorEnabled } from '../hooks/useCommunityModeratorEnabled'
import type { CommunitySummary, CommunityMessageAttachment } from '../services/communityService'
import { COMMUNITY_ATTACHMENT_ACCEPT } from '../utils/communityAttachments'
import {
  prepareCommunityMessageContent,
  stripEditedSuffixFromHtml,
} from '../utils/communityMessageContent'
import { CommunityDraftAttachmentsPreview } from './CommunityDraftAttachmentsPreview'
import { EmojiPicker } from './EmojiPicker'
import { CommunityEmojiIcon } from './CommunityEmojiIcon'
import { CommunityCardReactionsRow } from './CommunityCardReactionsRow'
import { CommunityMessageAttachments } from './CommunityMessageAttachments'
import { SmilePlus } from 'lucide-react'
import {
  computeEmojiPickerPlacement,
  type EmojiPickerAlign,
  type EmojiPickerPlacement,
} from '@/shared/ui/emojiPickerPlacement'
import { extractMentionUserIdsFromHtml } from '@/shared/utils/mentions'
import {
  buildLinkPreview,
  extractFirstUrlFromHtml,
  type CommunityLinkPreview,
} from '../services/linkPreviewService'
import { CommunityLinkPreviewCard } from './CommunityLinkPreviewCard'
import { CommunityMessageText } from './CommunityMessageText'
import { useCommunityMentionUsers } from '../hooks/useCommunityMentionUsers'
import { useCommunityMessageFocus } from '../hooks/useCommunityMessageFocus'
import { CommunityThreadNotificationsToggle } from './CommunityThreadNotificationsToggle'
import { useCommunityThreadPresenceReporter } from '../hooks/useCommunityThreadPresenceReporter'

const DEFAULT_EMOJIS: string[] = []
const CARD_EMOJIS = ['ðŸ‘', 'ðŸš€', 'ðŸ”¥']

type EntryProps =
  | {
      opportunityId: number
      symbol: string
      name?: string | null
      logoUrl?: string | null
      enabled: boolean
      summary?: CommunitySummary | null
      renderFooter?: (summaryRow: React.ReactNode) => React.ReactNode
      overlayContent?: React.ReactNode
      focusMessageId?: number | string | null
      forceOpen?: false
      onOpenChange?: (open: boolean) => void
      threadHook?: never
    }
  | {
      opportunityId: number
      symbol: string
      name?: string | null
      logoUrl?: string | null
      enabled: boolean
      summary?: CommunitySummary | null
      renderFooter?: (summaryRow: React.ReactNode) => React.ReactNode
      overlayContent?: React.ReactNode
      focusMessageId?: number | string | null
      forceOpen: boolean
      onOpenChange: (open: boolean) => void
      threadHook: ReturnType<typeof useCommunityThread>
    }

export function OpportunityCommunityEntry({
  opportunityId,
  symbol,
  name,
  logoUrl,
  enabled,
  summary,
  renderFooter,
  overlayContent,
  focusMessageId,
  forceOpen,
  onOpenChange,
  threadHook: threadHookFromProps,
}: EntryProps) {
  const { user, profile } = useAuth()
  const [openLocal, setOpenLocal] = React.useState(false)
  const [cardEmojiPickerOpen, setCardEmojiPickerOpen] = React.useState(false)
  const cardEmojiButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const [cardEmojiPickerPlacement, setCardEmojiPickerPlacement] =
    React.useState<EmojiPickerPlacement>('top')
  const [cardEmojiPickerAlign, setCardEmojiPickerAlign] =
    React.useState<EmojiPickerAlign>('left')
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [isVisible, setIsVisible] = React.useState(false)
  const [maxCardReactions, setMaxCardReactions] = React.useState(8)
  const isLg =
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : window.matchMedia('(min-width: 1024px)').matches

  React.useEffect(() => {
    if (typeof document === 'undefined') return
    if (document.querySelector('style[data-message-editor-autogrow=\"true\"]')) return
    const style = document.createElement('style')
    style.setAttribute('data-message-editor-autogrow', 'true')
    style.innerHTML = `
      .message-editor .ck.ck-editor__editable_inline {
        height: auto !important;
        min-height: 2.4em !important;
        max-height: min(40vh, 260px) !important;
        overflow-y: auto !important;
      }
    `
    document.head.appendChild(style)
  }, [])

  React.useEffect(() => {
    if (isVisible) return

    const el = containerRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        })
      },
      { rootMargin: '200px 0px 0px 0px' },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [isVisible])

  React.useEffect(() => {
    if (typeof window === 'undefined') return

    const updateMaxReactions = () => {
      const width =
        window.innerWidth || document.documentElement.clientWidth || 0
      // VersÃ£o mobile: atÃ© ~768px usamos 6 emojis, acima disso 8
      setMaxCardReactions(width <= 768 ? 6 : 8)
    }

    updateMaxReactions()
    window.addEventListener('resize', updateMaxReactions)
    return () => window.removeEventListener('resize', updateMaxReactions)
  }, [])

  const currentUserName = profile?.full_name || user?.email || 'UsuÃ¡rio'
  const currentUserAvatar = (profile as any)?.avatar_url ?? null
  const currentUserId = user?.id ?? null
  const isMaster = profile?.role === 'master'
  const moderatorEnabled = useCommunityModeratorEnabled(
    currentUserId ? String(currentUserId) : null,
    enabled && !isMaster,
  )
  const isModerator = isMaster || !!moderatorEnabled

  const effectiveOpen = typeof forceOpen === 'boolean' ? forceOpen : openLocal

  const threadHook =
    threadHookFromProps ||
    useCommunityThread(
      opportunityId,
      { name: currentUserName, avatarUrl: currentUserAvatar },
      enabled && isVisible && effectiveOpen,
    )

  const cardReactionsHook = useCommunityCardReactions(
    opportunityId,
    enabled && isVisible,
  )

  if (!enabled) return null

  const summaryToUse = summary

  function handleOpen(e: React.MouseEvent) {
    e.stopPropagation()
    if (onOpenChange) {
      onOpenChange(true)
    } else {
      setOpenLocal(true)
    }
  }

  const primaryAvatar = summaryToUse?.avatars?.[0]
  const secondaryAvatars =
    summaryToUse && summaryToUse.avatars.length > 1 ? summaryToUse.avatars.slice(1, 4) : []

  const extraCount =
    summaryToUse && summaryToUse.avatars.length > 4 ? summaryToUse.avatars.length - 4 : 0

  const responsesText =
    summaryToUse && summaryToUse.messagesCount > 0
      ? `${summaryToUse.messagesCount} resposta${summaryToUse.messagesCount > 1 ? 's' : ''}`
      : 'Iniciar conversa'

  const lastLabel = formatLastInteractionLabel(summaryToUse?.lastMessageAt)

  const summaryRow = (
    <div className="mt-2 flex items-center gap-2 text-xs text-foreground/80">
      {primaryAvatar ? (
        <div className="flex items-center gap-1">
          <Avatar
            src={primaryAvatar.authorAvatarUrl || undefined}
            alt={primaryAvatar.authorName || 'UsuÃ¡rio'}
            className="h-6 w-6 border-none"
            fallback={primaryAvatar.authorName?.[0] || 'U'}
          />
          {secondaryAvatars.map((av) => (
            <Avatar
              key={av.authorId}
              src={av.authorAvatarUrl || undefined}
              alt={av.authorName || 'UsuÃ¡rio'}
              className="h-5 w-5 -ml-2 border-none"
              fallback={av.authorName?.[0] || 'U'}
            />
          ))}
          {extraCount > 0 && (
            <div className="-ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px]">
              +{extraCount}
            </div>
          )}
        </div>
      ) : (
        <div className="h-6 w-6 rounded-full bg-muted" />
      )}

      <button
        type="button"
        onClick={handleOpen}
        className="text-[11px] font-semibold text-sky-400 hover:underline"
      >
        {responsesText}
      </button>

      {lastLabel && (
        <span className="text-[10px] text-[#575757]">
          {lastLabel}
        </span>
      )}
    </div>
  )

  const summaryContent = renderFooter ? renderFooter(summaryRow) : summaryRow

  return (
    <>
      <div ref={containerRef} className="mt-2 text-xs text-foreground/80">
        <CommunityCardReactionsRow
          reactions={cardReactionsHook.reactions}
          onToggleEmoji={(emoji) => cardReactionsHook.toggleEmoji(emoji)}
          maxVisibleReactions={maxCardReactions}
          renderAfter={(
            <div className="relative inline-flex">
              <button
                type="button"
                ref={cardEmojiButtonRef}
                onClick={(e) => {
                  e.stopPropagation()
                  try {
                    if (typeof window !== 'undefined' && cardEmojiButtonRef.current) {
                      const buttonRect = cardEmojiButtonRef.current.getBoundingClientRect()
                      const { placement, align } = computeEmojiPickerPlacement({
                        buttonRect,
                        containerRect: null,
                        estimatedPickerHeight: 520,
                        pickerWidth: 320,
                      })
                      setCardEmojiPickerPlacement(placement)
                      setCardEmojiPickerAlign(align)
                    }
                  } catch {
                    // fallback silencioso
                  }
                  setCardEmojiPickerOpen((prev) => !prev)
                }}
                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1f1f1f] text-[#b3b3b3] hover:bg-[#262626] hover:text-[#e5e5e5]"
              >
                <SmilePlus className="h-4 w-4" />
              </button>
              {cardEmojiPickerOpen && isLg && (
                <div
                  className={[
                    'absolute z-[120]',
                    cardEmojiPickerPlacement === 'top'
                      ? 'bottom-[calc(100%+0.5rem)]'
                      : 'top-7',
                    cardEmojiPickerAlign === 'right' ? 'right-0' : 'left-0',
                  ].join(' ')}
                  onClick={(event) => event.stopPropagation()}
                >
                  <EmojiPicker
                    onSelect={async (emoji) => {
                      await cardReactionsHook.toggleEmoji(emoji)
                      setCardEmojiPickerOpen(false)
                    }}
                    onClose={() => setCardEmojiPickerOpen(false)}
                    canDeleteCustomEmojis={!!isMaster}
                  />
                </div>
              )}
              {!isLg && (
                <BottomDrawer
                  open={cardEmojiPickerOpen}
                  onOpenChange={setCardEmojiPickerOpen}
                  header="Emojis"
                  className="h-[50vh] max-h-[50vh] bg-[#111111]"
                  zIndexClass="z-[180000]"
                  overlayClassName="bg-black/60 backdrop-blur-sm"
                >
                  <EmojiPicker
                    className="w-full"
                    onSelect={async (emoji) => {
                      await cardReactionsHook.toggleEmoji(emoji)
                      setCardEmojiPickerOpen(false)
                    }}
                    onClose={() => setCardEmojiPickerOpen(false)}
                    canDeleteCustomEmojis={!!isMaster}
                  />
                </BottomDrawer>
              )}
            </div>
          )}
        />
      </div>

      {summaryContent}

      <CommunityDrawer
        open={effectiveOpen}
        onOpenChange={onOpenChange ?? setOpenLocal}
        opportunityId={opportunityId}
        symbol={symbol}
        name={name}
        logoUrl={logoUrl}
        threadHook={threadHook}
        currentUserId={currentUserId}
        isMaster={!!isMaster}
        isModerator={!!isModerator}
        focusMessageId={focusMessageId}
        overlayContent={overlayContent}
      />
    </>
  )
}

type DrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunityId: number
  symbol: string
  name?: string | null
  logoUrl?: string | null
  threadHook: ReturnType<typeof useCommunityThread>
  currentUserId: string | null
  isMaster: boolean
  isModerator: boolean
  focusMessageId?: number | string | null
  overlayContent?: React.ReactNode
}

  function CommunityDrawer({
     open,
     onOpenChange,
     opportunityId,
    symbol,
    name,
    logoUrl,
    threadHook,
    currentUserId,
  isMaster,
  isModerator,
  focusMessageId,
  overlayContent,
  }: DrawerProps) {
   const isLg =
     typeof window === 'undefined' || typeof window.matchMedia !== 'function'
       ? true
       : window.matchMedia('(min-width: 1024px)').matches

   useCommunityThreadPresenceReporter({
     active: open,
     contextType: 'opportunity',
     contextId: opportunityId,
   })

   const {
     thread,
     loading,
    loadedOnce,
    sending,
    sendMessage,
    toggleCardEmoji,
    toggleMessageEmoji,
    deleteMessage,
  } = threadHook

  const mentionUserIds = React.useMemo(() => {
    const ids = new Set<string>()
    thread.messages.forEach((msg) => {
      const html = String(msg.content || '')
      extractMentionUserIdsFromHtml(html).forEach((id) => ids.add(id))
    })
    return Array.from(ids)
  }, [thread.messages])

  const mentionUsersById = useCommunityMentionUsers(mentionUserIds)
  const [editorValue, setEditorValue] = React.useState('')
  const [linkPreview, setLinkPreview] = React.useState<CommunityLinkPreview | null>(null)
  const [editingMessageId, setEditingMessageId] = React.useState<number | null>(null)
  const [openEmojiForMessageId, setOpenEmojiForMessageId] = React.useState<number | null>(null)
  const [emojiPickerPlacement, setEmojiPickerPlacement] = React.useState<'top' | 'bottom'>('bottom')
  const [emojiPickerAlign, setEmojiPickerAlign] = React.useState<'left' | 'right'>('left')
  const { formatUserDateTime } = useUserDateFormatter()
  const messagesContainerRef = React.useRef<HTMLDivElement | null>(null)
  const { highlightedMessageId, disableAutoScrollToBottom } =
    useCommunityMessageFocus({
      open,
      focusMessageId,
      messages: thread.messages,
      containerRef: messagesContainerRef as any,
    })
  const {
    attachments: draftAttachments,
    addFiles: addDraftFiles,
    removeAttachment,
    clear: clearDraftAttachments,
    error: attachmentError,
  } = useCommunityDraftAttachments()
  const [lightboxOpen, setLightboxOpen] = React.useState(false)
  const [lightboxAttachments, setLightboxAttachments] = React.useState<CommunityMessageAttachment[]>([])
  const [lightboxIndex, setLightboxIndex] = React.useState(0)

  const handleOpenLightbox = React.useCallback(
    (attachments: CommunityMessageAttachment[], index: number) => {
      if (!attachments || attachments.length === 0) return
      const normalized = attachments.filter((att) => att && att.url)
      if (!normalized.length) return
      const safeIndex = Math.max(0, Math.min(index, normalized.length - 1))
      setLightboxAttachments(normalized)
      setLightboxIndex(safeIndex)
      setLightboxOpen(true)
    },
    [],
  )

  const handleFilesAdded = React.useCallback(
    (files: File[]) => {
      addDraftFiles(files)
    },
    [addDraftFiles],
  )

  const handleSend = React.useCallback(() => {
    const html = String(editorValue || '')
    const prepared = prepareCommunityMessageContent(html)
    const filesToSend = draftAttachments.map((item) => item.file)
    if ((!prepared.text && !filesToSend.length) || sending) return

    // Limpa o editor imediatamente para o usuÃ¡rio
    setEditorValue('')
    setLinkPreview(null)
    clearDraftAttachments()

    // EdiÃ§Ã£o de mensagem existente
    if (editingMessageId != null) {
      if (typeof threadHook.editMessage === 'function') {
        void threadHook.editMessage(editingMessageId, html)
      }
      setEditingMessageId(null)
      return
    }

    // Nova mensagem com anexos
    void sendMessage(html, filesToSend)
  }, [
    clearDraftAttachments,
    draftAttachments,
    editingMessageId,
    editorValue,
    sendMessage,
    sending,
    threadHook,
  ])

  // Sempre que abrir o drawer ou chegar uma nova mensagem,
  // mantÃ©m o scroll posicionado na mensagem mais recente.
  React.useEffect(() => {
    if (!open) {
      clearDraftAttachments()
      setLightboxOpen(false)
      setLightboxAttachments([])
      setLightboxIndex(0)
      setLinkPreview(null)
      setEditingMessageId(null)
      setOpenEmojiForMessageId(null)
    }
  }, [clearDraftAttachments, open])

  React.useEffect(() => {
    if (!open) return
    if (disableAutoScrollToBottom) return
    const container = messagesContainerRef.current
    if (!container) return

    const scrollToBottom = () => {
      try {
        container.scrollTop = container.scrollHeight
      } catch {
        // ignora falhas de layout transitÃ³rias
      }
    }

    if (typeof window !== 'undefined' && 'requestAnimationFrame' in window) {
      const handle = window.requestAnimationFrame(scrollToBottom)
      return () => window.cancelAnimationFrame(handle)
    }

    scrollToBottom()
  }, [disableAutoScrollToBottom, open, thread.messages.length])

  React.useEffect(() => {
    if (!open) {
      setLinkPreview(null)
      return
    }
    const url = extractFirstUrlFromHtml(editorValue || '')
    if (!url) {
      setLinkPreview(null)
      return
    }
    const preview = buildLinkPreview(url)
    setLinkPreview(preview)
  }, [editorValue, open])

  const header = (
    <div className="flex items-center gap-2">
      {logoUrl ? (
        <img src={logoUrl} alt={symbol} className="h-6 w-6 rounded-full object-cover" />
      ) : (
        <div className="h-6 w-6 rounded-full bg-muted" />
      )}
      <div className="flex flex-col leading-tight">
        <span className="text-sm font-semibold">
          Conversa - {symbol}
        </span>
        {name && (
          <span className="text-[11px] text-foreground/70">
            {name}
          </span>
        )}
      </div>
    </div>
  )

  const headerRight = (
    <CommunityThreadNotificationsToggle
      contextType="opportunity"
      contextId={opportunityId}
      active={open}
    />
  )

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    handleSend()
  }

  const hasLightbox = lightboxOpen && lightboxAttachments.length > 0
  const lightboxTotal = lightboxAttachments.length
  const lightboxCurrentIndex =
    hasLightbox && lightboxTotal > 0
      ? Math.max(0, Math.min(lightboxIndex, lightboxTotal - 1))
      : 0
  const lightboxCurrent =
    hasLightbox && lightboxTotal > 0 ? lightboxAttachments[lightboxCurrentIndex] : null

  const overlaySlot =
    isLg && overlayContent ? (
      <div className="pointer-events-none absolute inset-0">
        <div className="pointer-events-auto flex h-full w-full items-start justify-center px-6 py-6 pr-[30vw]">
          <div
            className="max-h-[calc(100vh-3rem)] w-full max-w-3xl overflow-y-auto"
            onClick={(event) => event.stopPropagation()}
          >
            {overlayContent}
          </div>
        </div>
      </div>
    ) : null

  const content = (
    <div className="relative flex h-full flex-col gap-3">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[#1a1a1a]" />
      <div className="hidden">
        <span>Reações do card:</span>
        <CommunityCardReactionsRow
          reactions={thread.cardReactions}
          onToggleEmoji={toggleCardEmoji}
        />
      </div>

      <div
        ref={messagesContainerRef}
        className="mt-1 flex-1 space-y-4 overflow-y-auto overflow-x-hidden pr-1"
      >
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground/70">
          <span>Reações do card:</span>
          <CommunityCardReactionsRow
            reactions={thread.cardReactions}
            onToggleEmoji={toggleCardEmoji}
          />
        </div>

        {loading && !loadedOnce && thread.messages.length === 0 && (
          <div className="text-xs text-foreground/70">Carregando conversas...</div>
        )}
        {loadedOnce && thread.messages.length === 0 && (
          <div className="text-xs text-foreground/70">
            Nenhuma mensagem por enquanto. Seja o primeiro a comentar esta oportunidade.
          </div>
        )}
        {thread.messages.map((msg) => {
          const isAuthor = currentUserId && String(msg.authorId) === String(currentUserId)
          const canDelete = isModerator || isAuthor
          const canEdit = !!isAuthor
          const isPickerOpen = openEmojiForMessageId === msg.id
          const isHighlighted = Number(highlightedMessageId) === Number(msg.id)

          return (
            <div
              key={msg.id}
              data-community-message-id={msg.id}
              className={[
                'group flex items-start gap-2 rounded-md px-2 py-2 transition-colors duration-150 hover:bg-[#202020]',
                isHighlighted ? 'ring-2 ring-sky-500/60 bg-sky-500/5 hover:bg-sky-500/10' : '',
              ].join(' ')}
            >
              <Avatar
                src={msg.authorAvatarUrl || undefined}
                alt={msg.authorName}
                className="h-7 w-7"
                fallback={msg.authorName?.[0] || 'U'}
              />
              <div className="flex-1">
                <div className="flex items-baseline gap-2 text-[11px]">
                  <span className="font-semibold text-foreground">
                    {msg.authorName || 'UsuÃ¡rio'}
                  </span>
                  {msg.createdAt && (
                    <span className="text-[#454545]">
                      {formatUserDateTime(msg.createdAt)}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[13px] text-foreground/90 font-normal">
                  {(() => {
                    const raw = String(msg.content || '')
                    const { html: base, isEdited } = stripEditedSuffixFromHtml(raw)
                    return (
                      <>
                        <CommunityMessageText
                          text={base}
                          mentionUsersById={mentionUsersById}
                        />
                        {isEdited && (
                          <span className="whitespace-pre-line italic font-thin text-[#454545]">
                            {' '}
                            (editado)
                          </span>
                        )}
                      </>
                    )
                  })()}
                </div>
                <CommunityMessageAttachments
                  attachments={msg.attachments}
                  onOpenLightbox={handleOpenLightbox}
                />
                {(() => {
                  const url = extractFirstUrlFromHtml(String(msg.content || ''))
                  const preview = url ? buildLinkPreview(url) : null
                  if (!preview) return null
                  return (
                    <div className="mt-2">
                      <CommunityLinkPreviewCard preview={preview} />
                    </div>
                  )
                })()}
                <div className="mt-1 flex flex-col gap-1 text-[12px]">
                  <div className="flex flex-wrap items-center gap-2">
                    {msg.reactions.map((r) => (
                      <button
                        key={r.emoji}
                        type="button"
                        onClick={() => toggleMessageEmoji(msg.id, r.emoji)}
                        className={[
                          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
                          r.reactedByCurrentUser
                            ? 'border-emerald-400 bg-emerald-500/10'
                            : 'border-transparent bg-[#454545]',
                        ].join(' ')}
                      >
                        <CommunityEmojiIcon emoji={r.emoji} loading="eager" showFallback />
                        <span>{r.count}</span>
                      </button>
                    ))}
                    {/* Container relativo para o menu flutuante de emojis */}
                    <div className="relative inline-flex">
                      <button
                        type="button"
                        onClick={(event) => {
                          // toggle se jÃ¡ estiver aberto para esta mensagem
                          const alreadyOpen = openEmojiForMessageId === msg.id
                          if (alreadyOpen) {
                            setOpenEmojiForMessageId(null)
                            return
                          }

                          if (!isLg) {
                            setOpenEmojiForMessageId(msg.id)
                            return
                          }

                          // Decide se deve abrir para cima ou para baixo,
                          // e se deve alinhar Ã  esquerda ou Ã  direita,
                          // considerando os limites do container de mensagens
                          const containerEl = messagesContainerRef.current
                          const buttonRect = event.currentTarget.getBoundingClientRect()

                          const containerRect = containerEl
                            ? containerEl.getBoundingClientRect()
                            : null
                          const { placement, align } = computeEmojiPickerPlacement({
                            buttonRect,
                            containerRect,
                            estimatedPickerHeight: 520,
                            pickerWidth: 320,
                          })

                          setEmojiPickerPlacement(placement)
                          setEmojiPickerAlign(align)
                          setOpenEmojiForMessageId(msg.id)
                        }}
                        className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1f1f1f] text-[#b3b3b3] hover:bg-[#262626] hover:text-[#e5e5e5]"
                      >
                        <SmilePlus className="h-4 w-4" />
                      </button>
                      {isPickerOpen && isLg && (
                        <div
                          className={[
                            'absolute z-[90]',
                            emojiPickerPlacement === 'top'
                              ? 'bottom-[calc(100%+0.5rem)]'
                              : 'top-7',
                            emojiPickerAlign === 'right' ? 'right-0' : 'left-0',
                          ].join(' ')}
                        >
                          <EmojiPicker
                            onSelect={async (emoji) => {
                              // eslint-disable-next-line no-console
                              console.log('[Community] EmojiPicker onSelect', {
                                messageId: msg.id,
                                emoji,
                              })
                              await toggleMessageEmoji(msg.id, emoji)
                              setOpenEmojiForMessageId(null)
                            }}
                            onClose={() => setOpenEmojiForMessageId(null)}
                            canDeleteCustomEmojis={!!isMaster}
                          />
                        </div>
                      )}
                    </div>
                    {!isLg && (
                      <BottomDrawer
                        open={isPickerOpen}
                        onOpenChange={(next) => {
                          setOpenEmojiForMessageId(next ? msg.id : null)
                        }}
                        header="Emojis"
                        className="h-[50vh] max-h-[50vh] bg-[#111111]"
                        zIndexClass="z-[180000]"
                        overlayClassName="bg-black/60 backdrop-blur-sm"
                      >
                        <EmojiPicker
                          className="w-full"
                          onSelect={async (emoji) => {
                            await toggleMessageEmoji(msg.id, emoji)
                            setOpenEmojiForMessageId(null)
                          }}
                          onClose={() => setOpenEmojiForMessageId(null)}
                          canDeleteCustomEmojis={!!isMaster}
                        />
                      </BottomDrawer>
                    )}
                    {(canEdit || canDelete) && (
                      <div className="ml-3 flex items-center gap-2 text-[10px] opacity-0 transition-opacity group-hover:opacity-100">
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => {
                              const raw = String(msg.content || '')
                              const base = stripEditedSuffixFromHtml(raw).html
                              setEditorValue(base)
                              clearDraftAttachments()
                               setEditingMessageId(msg.id)
                             }}
                             className="font-medium text-sky-400 hover:underline"
                           >
                            Editar
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => deleteMessage(msg.id)}
                            className="font-medium text-red-400 hover:text-red-300"
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {hasLightbox && lightboxCurrent && (
        <div className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/80 px-4 py-6">
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute right-4 top-4 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white hover:bg-black/80"
          >
            Fechar
          </button>

          {lightboxTotal > 1 && (
            <button
              type="button"
              onClick={() => {
                if (!hasLightbox || lightboxTotal <= 1) return
                setLightboxIndex((prev) =>
                  prev <= 0 ? lightboxTotal - 1 : prev - 1,
                )
              }}
              className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
            >
              â€¹
            </button>
          )}

          <div className="max-h-[80vh] max-w-[80vw]">
            <img
              src={lightboxCurrent.url}
              alt="Imagem da mensagem da comunidade"
              className="h-full w-full rounded-lg object-contain shadow-2xl"
            />
          </div>

          {lightboxTotal > 1 && (
            <button
              type="button"
              onClick={() => {
                if (!hasLightbox || lightboxTotal <= 1) return
                setLightboxIndex((prev) =>
                  prev >= lightboxTotal - 1 ? 0 : prev + 1,
                )
              }}
              className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
            >
              â€º
            </button>
          )}

          {lightboxTotal > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
              {lightboxCurrentIndex + 1} / {lightboxTotal}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 h-px bg-[#161616]" />
      <form onSubmit={handleSubmit} className="mt-2 space-y-2 pt-2">
        {attachmentError && (
          <div className="text-[11px] text-red-400">
            {attachmentError}
          </div>
        )}
        {linkPreview && (
          <div className="mb-2">
            <CommunityLinkPreviewCard preview={linkPreview} />
          </div>
        )}
        <CommunityDraftAttachmentsPreview
          attachments={draftAttachments}
          onRemove={removeAttachment}
        />
        <MessageEditor
          variant="slack"
          enableMentions
          value={editorValue}
          onChange={setEditorValue}
          placeholder="Adicionar uma resposta"
          sendOnEnter
          onSubmit={handleSend}
          submitDisabled={
            sending ||
            (!prepareCommunityMessageContent(editorValue || '').text &&
              !draftAttachments.length)
          }
          toolbar={[]}
          onPasteFiles={handleFilesAdded}
          onPickFiles={handleFilesAdded}
          pickFilesAccept={COMMUNITY_ATTACHMENT_ACCEPT}
          pickFilesMultiple
          stripImages
          renderEmojiPicker={({ onSelect, onClose, className }) => (
            <EmojiPicker
              className={className}
              onSelect={onSelect}
              onClose={onClose}
              canDeleteCustomEmojis={!!isMaster}
            />
          )}
        />
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] text-foreground/60">
            Use a comunidade para tirar dÃºvidas rÃ¡pidas sobre esta oportunidade. Evite compartilhar
            dados sensÃ­veis.
          </div>
          <Button
            type="submit"
            disabled={
              sending ||
              (!prepareCommunityMessageContent(editorValue || '').text &&
                !draftAttachments.length)
            }
            className="h-8 px-3 text-xs hidden"
          >
            Enviar
          </Button>
        </div>
      </form>
    </div>
  )

  return isLg ? (
    <RightDrawer
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      headerRight={headerRight}
      className="w-[30vw] max-w-none bg-[#1a1a1a]"
      overlayClassName="bg-black/40 backdrop-blur-md"
      variant="high"
      overlayContent={overlaySlot}
    >
      {content}
    </RightDrawer>
  ) : (
    <BottomDrawer
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      headerRight={headerRight}
      className="h-[100dvh] max-h-[100vh] bg-[#1a1a1a]"
      zIndexClass="z-[100000]"
      overlayClassName="bg-black/40 backdrop-blur-md"
    >
      {content}
    </BottomDrawer>
  )
}

function formatLastInteractionLabel(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMin / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMin < 1) return 'Última resposta agora há pouco'
  if (diffHours < 24 && now.toDateString() === date.toDateString()) {
    const hh = String(date.getHours()).padStart(2, '0')
    const mm = String(date.getMinutes()).padStart(2, '0')
    return `Última resposta hoje às ${hh}h${mm}`
  }
  if (diffDays === 1) return 'Última resposta há 1 dia'
  if (diffDays > 1) return `Última resposta há ${diffDays} dias`
  return null
}

function formatLastInteraction(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHours = Math.floor(diffMin / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMin < 1) return 'Ãšltima resposta agora hÃ¡ pouco'
  if (diffHours < 24 && now.toDateString() === date.toDateString()) {
    const hh = String(date.getHours()).padStart(2, '0')
    const mm = String(date.getMinutes()).padStart(2, '0')
    return `Ãšltima resposta hoje Ã s ${hh}h${mm}`
  }
  if (diffDays === 1) return 'Ãšltima resposta hÃ¡ 1 dia'
  if (diffDays > 1) return `Ãšltima resposta hÃ¡ ${diffDays} dias`
  return null
}
